import { useForm } from 'react-hook-form';
import {
    emptyRejectApplicationForm,
    IRejectApplicationForm,
    RejectApplicationFormValues,
    rejectApplicationSchema,
} from '../../../common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Form,
    FormInputText,
    FormSelector,
    Grid,
    PargodaRoundButton,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../../base';
import {
    jobApplicationActions,
    jobApplicationSelectors,
    organizationSelectors,
} from '../../../actions';
import PargodaModal from '../../PargodaModal';
import { useCallback, useEffect } from 'react';

const BulkRejectApplicationModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const methods = useForm<IRejectApplicationForm>({
        resolver: yupResolver(rejectApplicationSchema()),
        mode: 'onSubmit',
        defaultValues: emptyRejectApplicationForm,
    });

    const isRejectLoading = useAppSelector(
        jobApplicationSelectors.getIsBulkRejectApplicationLoading
    );

    const isModalOpen = useAppSelector(
        jobApplicationSelectors.getIsBulkRejectApplicationModalOpen
    );

    const bulkRejectModalData = useAppSelector(
        jobApplicationSelectors.getBulkRejectApplicationModalData
    );

    const rejectApplicationReasons = useAppSelector(
        organizationSelectors.getRejectApplicationReasons
    );

    const setIsModalOpen = useCallback(
        (isModalOpen: boolean) => {
            dispatch(
                jobApplicationActions.setIsBulkRejectApplicationModalOpen(
                    isModalOpen
                )
            );
        },
        [dispatch]
    );

    useEffect(() => {
        if (isModalOpen) {
            methods.reset(emptyRejectApplicationForm);
        }
    }, [isModalOpen, methods]);

    const { control, trigger, watch } = methods;

    const reasonOption = watch(RejectApplicationFormValues.reasonOption);
    const reasonText = watch(RejectApplicationFormValues.reasonText);

    useEffect(() => {
        if (methods.formState.isSubmitted) {
            trigger(RejectApplicationFormValues.reasonText);
        }
    }, [reasonOption, trigger, methods.formState.isSubmitted]);

    useEffect(() => {
        if (methods.formState.isSubmitted) {
            trigger(RejectApplicationFormValues.reasonOption);
        }
    }, [reasonText, trigger, methods.formState.isSubmitted]);

    const rejectSubmit = () => {
        const formData = methods.getValues();
        dispatch(
            jobApplicationActions.bulkRejectApplication({
                applicationIds: bulkRejectModalData?.applicationIds || [],
                reasonOption: formData[RejectApplicationFormValues.reasonOption] || null,
                reasonText: formData[RejectApplicationFormValues.reasonText] || null,
            })
        );
    };

    const candidateCount = bulkRejectModalData?.applicationIds?.length || 0;

    return (
        <PargodaModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            modalTitle={t('jobApplicationPage.bulkRejectModalTitle')}
            sx={{ width: '595px' }}
        >
            <Form methods={methods} onSubmit={rejectSubmit}>
                <Grid container>
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12} pt={1}>
                                <Typography
                                    variant="body1"
                                    color={theme.palette.grey[600]}
                                >
                                    {t(
                                        'jobApplicationPage.bulkRejectModalDescription',
                                        { count: candidateCount }
                                    )}
                                </Typography>
                            </Grid>
                            <Grid container spacing={2} pt={3}>
                                <Grid item xs={12}>
                                    <FormSelector
                                        name={
                                            RejectApplicationFormValues.reasonOption
                                        }
                                        control={control}
                                        options={rejectApplicationReasons}
                                        label={t(
                                            'jobApplicationPage.rejectOptionLabel'
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormInputText
                                        name={
                                            RejectApplicationFormValues.reasonText
                                        }
                                        control={control}
                                        label={t(
                                            'jobApplicationPage.rejectTextLabel'
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} pb={3}>
                                {methods.formState.errors[
                                    RejectApplicationFormValues.reasonOption
                                ] && (
                                    <Typography
                                        variant="caption"
                                        color={theme.palette.error.main}
                                    >
                                        {t(
                                            'jobApplicationPage.rejectReasonIsRequired'
                                        )}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                display="flex"
                                justifyContent="flex-end"
                                pb={2}
                            >
                                <PargodaRoundButton
                                    textColor={theme.palette.grey[600]}
                                    variant="outlined"
                                    loadingPosition="start"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                    }}
                                    startIcon={<></>}
                                    loading={isRejectLoading}
                                    sx={{
                                        marginInlineEnd: theme.spacing(1.5),
                                        height: '48px',
                                        width: '214px',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                    }}
                                >
                                    {t('jobApplicationPage.cancelButtonLabel')}
                                </PargodaRoundButton>
                            </Grid>
                            <Grid item xs={6} display="flex" pb={2}>
                                <PargodaRoundButton
                                    textColor={theme.palette.common.white}
                                    variant="contained"
                                    type="submit"
                                    loadingPosition="start"
                                    startIcon={<></>}
                                    loading={isRejectLoading}
                                    sx={{
                                        background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        width: '214px',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                    }}
                                >
                                    {t('jobApplicationPage.reject')}
                                </PargodaRoundButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Form>
        </PargodaModal>
    );
};

export default BulkRejectApplicationModal;
